import React, { useState, useRef, useEffect } from 'react';
import './App.css';
import { useForm } from 'react-hook-form';
import { setCookie, getCookie } from './cookieUtils';

const FIRST_API_URL = process.env.REACT_APP_FIRST_API_URL;
const EVENT_NAME = process.env.REACT_APP_EVENT_NAME;

function App() {
  const { register, handleSubmit, setValue, trigger, formState: { errors }, reset, watch } = useForm();
  const [submitted, setSubmitted] = useState(false);
  const [firstname, setFirstName] = useState('');
  const [emailProvidersVisible, setEmailProvidersVisible] = useState(false);
  const [emailBlurCount, setEmailBlurCount] = useState(0);
  const emailRef = useRef(null);

  const emailProviders = ['@gmail.com', '@yahoo.com', '@icloud.com', '@me.com', '@hotmail.com', '@aol.com'];

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const onsiteParam = urlParams.get('onsite');
    if (onsiteParam === 'true') {
      setCookie('onsite', 'true', 1); // Set cookie for 1 day
    }
  }, []);

  const onSubmit = async (data) => {
    setFirstName(data.firstname.trim());
    data.event = EVENT_NAME;  // Add event field to data object
    data.onsite = getCookie('onsite') === 'true';  // Add onsite field based on cookie

    try {
      // Send data to the first API
      const response1 = await fetch(FIRST_API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(data),
      });

      if (response1.ok) {
        setSubmitted(true);
        // Reset the form and view after 3 seconds
        setTimeout(() => {
          setSubmitted(false);
          reset(); // Reset the form fields
        }, 10000);
      } else {
        console.error('Failed to submit data to the registration API');
      }
    } catch (error) {
      console.error('An error occurred', error);
    }
  };

  const handleBlur = (field) => {
    if (field === 'email') {
      setEmailBlurCount(emailBlurCount + 1);
      if (emailBlurCount >= 1) {
        trigger(field); // Trigger validation only after the second blur
      }
    } else {
      trigger(field); // Trigger validation on blur for other fields
    }

    // Trim white spaces
    const value = document.getElementById(field).value.trim();
    setValue(field, value);
  };

  const formatPhoneNumber = (event) => {
    const input = event.target.value.replace(/\D/g, '').substring(0, 10); // Remove non-digits and limit to 10 digits
    const areaCode = input.substring(0, 3);
    const middle = input.substring(3, 6);
    const last = input.substring(6, 10);

    if (input.length > 6) {
      event.target.value = `(${areaCode}) ${middle} - ${last}`;
    } else if (input.length > 3) {
      event.target.value = `(${areaCode}) ${middle}`;
    } else if (input.length > 0) {
      event.target.value = `(${areaCode}`;
    }

    setValue('phone', event.target.value); // Update the value in the form state
  };

  const handleFocus = (field) => {
    if (field === 'email') {
      setEmailProvidersVisible(true);
    } else {
      setEmailProvidersVisible(false);
    }
  };

  const handleEmailInput = () => {
    setEmailProvidersVisible(true);
  };

  const handleEmailProviderClick = (provider) => {
    const emailInput = emailRef.current;
    const value = emailInput.value.trim();
    const atIndex = value.indexOf('@');
    const newValue = atIndex !== -1 ? value.substring(0, atIndex) + provider : value + provider;
    emailInput.value = newValue;
    setValue('email', newValue); // Update form state
    if (emailBlurCount >= 1) {
      trigger('email'); // Validate the email field only after the second blur
    }
    setEmailProvidersVisible(false);
  };

  const phone = watch('phone');

  if (submitted) {
    return (
      <div className="App">
        <header className="App-header">
          <img src="/images/myt-rr-header-logo-screen.png" className="App-logo" alt="logo" />
          <div className="success-message">
            <p>Thank you, {firstname}.</p>
            <p>You are now on the guest list.</p>
            <p>See you in East Hampton!</p><br/>
          </div>
        </header>
      </div>
    );
  }

  return (
    <div className="App">
      <header className="App-header">
        <img src="/images/myt-rr-header-logo-screen.png" className="App-logo" alt="logo" />
        <div className="event-info">
          <p>June 28 - August 18</p>
          <p>Thursdays - Sundays only</p>
          <p>11am to 6pm</p>
          <p>9 Railroad Ave.</p>
          <p>East Hampton, New York</p>
        </div>
        <form className="form" onSubmit={handleSubmit(onSubmit)}>
          <div className="form-group">
            <input
              id="firstname"
              type="text"
              placeholder="First Name"
              {...register("firstname", { required: true, pattern: /^[A-Za-z]+$/ })}
              onBlur={() => handleBlur('firstname')}
              onFocus={() => handleFocus('firstname')}
            />
            {errors.firstname && <span className="error">First Name is required and should be text only.</span>}
          </div>

          <div className="form-group">
            <input
              id="lastname"
              type="text"
              placeholder="Last Name"
              {...register("lastname", { required: true, pattern: /^[A-Za-z]+$/ })}
              onBlur={() => handleBlur('lastname')}
              onFocus={() => handleFocus('lastname')}
            />
            {errors.lastname && <span className="error">Last Name is required and should be text only.</span>}
          </div>

          <div className="form-group">
            <input
              id="email"
              type="email"
              placeholder="Email Address"
              {...register("email", {
                required: true,
                pattern: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/,
              })}
              onBlur={() => handleBlur('email')}
              onFocus={() => handleFocus('email')}
              onInput={handleEmailInput}
              ref={emailRef}
            />
            {errors.email && <span className="error">Valid email is required.</span>}
            {emailProvidersVisible && (
              <div className="email-providers">
                {emailProviders.map(provider => (
                  <button type="button" key={provider} onClick={() => handleEmailProviderClick(provider)}>
                    {provider}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="form-group">
            <input
              id="phone"
              type="text"
              placeholder="Phone Number (optional)"
              {...register("phone", { pattern: /^\(\d{3}\) \d{3} - \d{4}$/ })}
              onChange={formatPhoneNumber}
              onBlur={() => handleBlur('phone')}
              onFocus={() => handleFocus('phone')}
            />
            {errors.phone && <span className="error">Phone number should be in the format (xxx) xxx - xxxx.</span>}
          </div>

          <div className="form-group checkbox-group">
            <label>
              <input type="checkbox" defaultChecked {...register("disclaimer1")} />
              <span></span>
              <p>I will receive the Mytheresa Newsletter with selected offers. I can unsubscribe at any time free of charge at <a class="inline-link" href="mailto:privacy@mytheresa.com">privacy@mytheresa.com</a></p>
            </label>
            {errors.disclaimer1 && <span className="error">This field is required.</span>}
          </div>

          <div className="form-group checkbox-group">
            <label>
              <input type="checkbox" defaultChecked {...register("disclaimer2", { required: !!phone })} />
              <span></span>
              By entering my phone number and clicking submit, I consent to receive automated recurring mobile alerts to the number provided. Consent is not a condition of purchase. I can opt out at any time by texting STOP to 33565. I can also reply HELP for more information. Message and data rates may apply.
            </label>
          </div>

          {errors.disclaimer2 && <span className="error">Consent is required if phone number is provided.</span>}
          
          <div className="form-group checkbox-group">
            <label>
              <input type="checkbox" defaultChecked {...register("disclaimer3")} />
              <span></span>
              <p>By checking this box, you are consenting to be contacted by Porsche Cars North America, Inc. (PCNA), its affiliate companies and our authorized Porsche dealers. If you don't wish to be contacted in this manner, please do not provide your email and/or phone information, as applicable. To the extent permitted by law, your permission supersedes any prior opt-outs given to PCNA as well as any government registries of Do Not Call Lists. Your information may be used to inform you about Porsche products, services, news and events in accordance with our Privacy Notice, available at <a class="inline-link" href="https://www.porsche.com/usa/privacy-policy/">https://www.porsche.com/usa/privacy-policy/</a>. You may opt out at any time by calling 1-800-PORSCHE or by sending an email to privacy@porsche.us. California residents may view information about PCNA’s privacy practices and rights they may have under California law in our Privacy Notice, in the section titled “Additional Notice to California Consumers.”</p>
            </label>
          </div>


          {errors.disclaimer3 && <span className="error">Consent is required if phone number is provided.</span>}

          <button type="submit" className="form-button">Submit</button>
        </form>
      </header>
    </div>
  );
}

export default App;
